import React, { useRef, useState, useEffect } from "react";
import styled, { css } from "styled-components";
import { AnimatePresence, motion } from "framer-motion";
import SubmittedQuestion from "./SubmittedQuestion";
import { useAuth } from "firebase";

function QAndA() {
  const [submittedQuestions, setSubmittedQuestions] = useState([]);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [answer, setAnswer] = useState("");
  const [userQuestions, setUserQuestions] = useState([]);
  const [participate, setParticipate] = useState(false);
  const [formValues, setFormValues] = useState({
    name: "",
    uid: null,
    isAdmin: false,
    question: "",
  });
  const { firebase } = useAuth();
  const inputRef = useRef();

  useEffect(() => {
    const existingUser = JSON.parse(
      window.localStorage.getItem("parkinsons2026ExistingUser")
    );
    if (existingUser) {
      setParticipate(true);
      setFormValues({
        ...formValues,
        name: existingUser.name,
        uid: existingUser.uid,
        isAdmin: false,
      });
    }
  }, []);

  useEffect(() => {
    let unsubscribeFromUser;
    if (firebase && formValues.uid !== null) {
      unsubscribeFromUser = firebase.subscribeToUser({
        snapshot: (snapshot) => {
          if (!snapshot.empty) {
            setFormValues({
              ...formValues,
              name: snapshot.data()?.name,
              isAdmin: snapshot.data()?.isAdmin,
            });
          } else if (snapshot.empty) {
            console.log("No user account created");
          }
        },
        uid: formValues.uid,
      });
    }
    return () => {
      if (unsubscribeFromUser) {
        unsubscribeFromUser();
      }
    };
  }, [firebase, formValues.uid]);

  useEffect(() => {
    let unsubscribeFromQuestions;
    if (firebase) {
      unsubscribeFromQuestions = firebase.subscribeToQuestions({
        snapshot: (snapshot) => {
          console.log(snapshot);
          if (!snapshot.empty) {
            const _allQuestions = [];
            snapshot.forEach((doc) => {
              _allQuestions.push({
                qid: doc.id,
                ...doc.data(),
              });
            });
            setSubmittedQuestions(_allQuestions);
          } else if (snapshot.empty) {
            console.log("No questions");
            setSubmittedQuestions([]);
          }
        },
      });
    }
    return () => {
      if (unsubscribeFromQuestions) {
        unsubscribeFromQuestions();
      }
    };
  }, [firebase]);

  useEffect(() => {
    if (submittedQuestions.length > 0) {
      const _userQuestions = submittedQuestions.reduce(
        (accumulator, currentValue) => {
          if (currentValue.uid === formValues.uid) {
            return [currentValue, ...accumulator];
          }
          return accumulator;
        },
        []
      );
      setUserQuestions(_userQuestions);
    } else {
      setUserQuestions([]);
    }
  }, [submittedQuestions]);

  const handleSubmitAnswer = (e) => {
    e.preventDefault();
    if (firebase && selectedQuestion) {
      firebase
        .submitAnswer({
          text: answer,
          qid: selectedQuestion.qid,
          uid: selectedQuestion.uid,
        })
        .then(() => {
          setFormValues({
            ...formValues,
            question: "",
          });
          setAnswer("");
          setSelectedQuestion(null);
        })
        .catch(console.error);
    }
  };

  const handleSubmitQuestion = (e) => {
    e.preventDefault();
    if (firebase) {
      firebase
        .submitNewQuestion({
          name: formValues.name,
          uid: formValues.uid,
          text: formValues.question,
        })
        .then(() => {
          setFormValues({
            ...formValues,
            question: "",
          });
        })
        .catch(console.error);
    }
  };

  const handleCreateAccount = (e) => {
    e.preventDefault();
    if (firebase) {
      firebase
        .createUserInDatabase({
          name: formValues.name,
        })
        .then((result) => {
          setFormValues({
            ...formValues,
            uid: result,
          });
          window.localStorage.setItem(
            "parkinsons2026ExistingUser",
            JSON.stringify({
              name: formValues.name,
              uid: result,
            })
          );
          setParticipate(true);
        })
        .catch(console.error);
    }
  };

  const handleTextInputChange = (event) => {
    event.persist();
    setFormValues({
      ...formValues,
      [event.target.name]: event.target.value,
    });
  };

  const handleEmojiClick = (emojiType) =>
    firebase.sendEmoji({ emojiType }).catch(console.error);

  return (
    <Container isModerator={formValues?.isAdmin}>
      {participate ? (
        <>
          {formValues.isAdmin ? (
            <p>Moderator&apos;s Control Panel</p>
          ) : (
            <p>
              Here you can submit questions, which may be discussed during the
              livestream.
            </p>
          )}
          <div>
            <SubmittedQuestions submittedQuestions={submittedQuestions}>
              <AnimatePresence initial={false}>
                {formValues.isAdmin
                  ? submittedQuestions?.map((submittedQuestion, index) => (
                      <SubmittedQuestion
                        key={submittedQuestion.qid}
                        index={index}
                        isAdmin={formValues.isAdmin}
                        submittedQuestion={submittedQuestion}
                        selectedQuestion={selectedQuestion}
                        setSelectedQuestion={setSelectedQuestion}
                        userQuestions={userQuestions}
                        formValues={formValues}
                      />
                    ))
                  : userQuestions?.map((submittedQuestion, index) => (
                      <SubmittedQuestion
                        key={submittedQuestion.qid}
                        index={index}
                        isAdmin={formValues.isAdmin}
                        submittedQuestion={submittedQuestion}
                        selectedQuestion={selectedQuestion}
                        setSelectedQuestion={setSelectedQuestion}
                        userQuestions={userQuestions}
                        formValues={formValues}
                      />
                    ))}
              </AnimatePresence>
            </SubmittedQuestions>
            <Form>
              <AnimatePresence initial={false}>
                {formValues.isAdmin ? (
                  <TextArea
                    ref={inputRef}
                    onChange={(e) => {
                      e.preventDefault();
                      setAnswer(e.target.value);
                    }}
                    value={answer}
                    id="answer"
                    name="answer"
                    type="text"
                    placeholder="Select a question then type your answer here to send an answer privately to the user."
                  />
                ) : (
                  <TextArea
                    ref={inputRef}
                    onChange={handleTextInputChange}
                    value={formValues.question}
                    id="question"
                    name="question"
                    type="text"
                    placeholder="Type your question..."
                  />
                )}
              </AnimatePresence>
              <Action>
                <AnimatePresence>
                  {formValues.isAdmin ? (
                    <Button
                      type="button"
                      onClick={(e) => handleSubmitAnswer(e)}
                      disabled={!selectedQuestion || answer.length < 1}
                    >
                      Submit Answer
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      onClick={(e) => handleSubmitQuestion(e)}
                      disabled={formValues.question.length < 1}
                      style={{ marginBottom: "1rem" }}
                    >
                      Submit Question
                    </Button>
                  )}
                </AnimatePresence>
              </Action>
            </Form>
          </div>
        </>
      ) : (
        <>
          <p>
            Enter your name in the field below
            <br /> to submit a question to our moderators
          </p>
          <div>
            <div />
            <Form>
              <NameInput
                onChange={handleTextInputChange}
                value={formValues.name}
                id="name"
                name="name"
                type="text"
                placeholder="Enter your name here..."
              />
              <Action>
                <AnimatePresence>
                  <Button
                    type="button"
                    onClick={(e) => handleCreateAccount(e)}
                    disabled={formValues.name.length < 2}
                    style={{ marginBottom: "1rem" }}
                  >
                    <p>Participate</p>
                  </Button>
                </AnimatePresence>
              </Action>
            </Form>
          </div>
        </>
      )}
    </Container>
  );
}

const Container = styled(motion.div).attrs({
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
})`
  background-color: #fff;
  padding: 0.75rem 0.75rem 0.5rem;
  position: relative;

  @media screen and (min-width: 410px) {
    padding: 1.275rem 1.0125rem 0.95rem;
  }

  @media screen and (min-width: 1150px) and (min-width: 1365px) {
    padding: 0.75rem 0.75rem 0.5rem;
  }

  @media screen and (min-width: 1366px) {
    padding: 1.275rem 1.0125rem 1.15rem;
  }

  > p {
    color: var(--abbvie-green);
    font-size: 0.875rem;
    font-style: ${({ isModerator }) => (isModerator ? "normal" : "italic")};
    font-weight: ${({ isModerator }) => (isModerator ? 600 : 300)};
    line-height: 1.25em;
    text-align: center;
    ${({ isModerator }) =>
      isModerator && `transform: scale3d(1.125, 1.125, 1.125)`}

    @media screen and (min-width: 410px) {
      font-size: 1rem;
      margin: 0.063em 0 1.25em;
    }

    @media screen and (min-width: 1150px) and (min-width: 1365px) {
      font-size: 0.875rem;
      margin: 0.5em 0 1em;
    }

    @media screen and (min-width: 1366px) {
      font-size: 1rem;
      margin: 0.063em 0 1.25em;
    }
  }

  > div {
    display: flex;
    flex-direction: column;
    height: calc(100% - 2.6rem);
    justify-content: space-between;

    @media screen and (min-width: 410px) {
      height: calc(100% - 2rem);
    }

    @media screen and (min-width: 1150px) and (min-width: 1365px) {
      height: calc(100% - 2.65rem);
    }

    @media screen and (min-width: 1366px) {
      height: calc(100% - 2.2rem);
    }
  }
`;

const Form = styled.div`
  border-top: 0.063rem solid #bdbdbd;

  > div {
    display: flex;
    justify-content: space-between;
    margin-top: 0.875rem;
  }
`;

const NameInput = styled.input`
  align-items: center;
  background-color: transparent;
  border: 0.063em solid var(--abbvie-green);
  border-radius: 2px;
  color: black;
  display: flex;
  font-family: "Lato", sans-serif;
  font-size: 1rem;
  font-weight: 400;
  margin-top: 0.5em;
  height: 2.5rem;
  justify-content: flex-start;
  letter-spacing: 0.5px;
  line-height: 2rem;
  outline-color: #c4c4c4;
  padding: 0.625rem;
  box-sizing: border-box;
  width: 100%;
  &:focus {
    outline-color: var(--abbvie-green);
  }
  &::placeholder {
    color: #c4c4c4;
    font-size: 1rem;
  }
`;

const TextArea = styled.textarea`
  border: 0.063em solid var(--abbvie-green);
  font-size: 1rem;
  height: 6.25em;
  margin-top: 0.5em;
  font-family: "Lato", sans-serif;
  outline-color: var(--abbvie-green);
  overflow-x: hidden;
  overflow-y: auto;
  padding: 0.625em;
  resize: none;
  width: 100%;

  &::placeholder {
    color: #c4c4c4;
    font-size: 1rem;
  }

  ::-webkit-scrollbar {
    width: 0.5rem;
  }

  ::-webkit-scrollbar-track {
    box-shadow: inset 0 0 0.31rem grey;
    border-radius: 0.625rem;
  }

  ::-webkit-scrollbar-thumb {
    background-color: var(--abbvie-green);
    border-radius: 0.625rem;
  }
`;

const SubmittedQuestions = styled.div`
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  margin-top: 1rem;
  ${({ submittedQuestions }) =>
    submittedQuestions &&
    submittedQuestions.length > 0 &&
    css`
      ::-webkit-scrollbar {
        width: 0.5rem;
      }

      ::-webkit-scrollbar-track {
        box-shadow: inset 0 0 0.31rem grey;
        border-radius: 0.625rem;
      }

      ::-webkit-scrollbar-thumb {
        background-color: var(--abbvie-green);
        border-radius: 0.625rem;
      }
    `}
`;

const Action = styled.div`
  display: flex;
  justify-content: flex-end;
  position: relative;
  > * {
    margin-left: 0.625rem;
  }
`;

const Button = styled.button`
  align-items: center;
  background-color: ${({ disabled }) =>
    disabled ? "rgba(196, 196, 196, 0.3)" : "#2a6440"};
  color: ${({ disabled }) => (disabled ? "#C4C4C4" : "white")};
  border-radius: 2px;
  cursor: pointer;
  display: flex;
  height: 2.5rem;
  font-weight: 700;
  justify-content: center;
  transition: background-color 0.25s ease-in-out, color 0.25s ease-in-out,
    outline-color 0.25s ease-in-out;
  width: 100%;
  max-width: 160px;
  margin-left: auto;
  text-transform: uppercase;
  &:focus {
    outline-color: black;
  }
`;

const EmojiSelector = styled(motion.div)`
  height: 3.25rem;
  position: absolute;
  right: -0.063rem;
  top: -3.125rem;
  width: 10rem;
  > div {
    background-color: #fff;
    box-shadow: 0px 0.25rem 0.625rem rgba(0, 0, 0, 0.14);
    display: flex;
    height: 2.5rem;
    justify-content: space-between;
    padding: 0.5rem 0.688rem;
    position: relative;
    > div {
      background-color: #fff;
      bottom: -0.25rem;
      height: 0.5rem;
      pointer-events: none;
      position: absolute;
      right: 0.875rem;
      transform: rotate(45deg);
      width: 0.5rem;
    }
  }
`;

const Emojis = styled(motion.div)`
  align-items: center;
  background-color: #fff;
  border: 1px solid var(--abbvie-green);
  border-radius: 2px;
  cursor: pointer;
  display: flex;
  height: 2.5rem;
  justify-content: center;
  position: relative;
  transition: background-color 0.25s ease-in-out, fill 0.25s ease-in-out;
  width: 2.5rem;
  &:hover {
    background-color: var(--abbvie-green);
    > svg path {
      fill: #fff;
    }
  }
`;

const Emoji = styled(motion.span).attrs({
  whileTap: {
    scale: 0.9,
  },
  whileHover: {
    scale: 1.1,
  },
})`
  display: inline-flex;
  svg {
    width: 1.5rem;
  }
`;

export default QAndA;
